<template>
  <div
    class="tw-rounded-sm tw-bg-white tw-px-4 tw-py-3 tw-h-fit"
    :data-test-id="`${tier}-quick_actions-card`"
  >
    <div class="tw-flex">
      <div class="tw-grow tw-pb-2">
        <div class="tw-text-lg tw-font-semibold">
          {{ t("quick-actions") }}
        </div>
      </div>
      <span
        v-tippy="tooltip"
        class="tw-text-primary-grey-200 tw-mt-3"
        :data-test-id="`${tier}-quick_actions_info-icon`"
      >
        <cb-icon-information :width="18" />
      </span>
    </div>
    <div>
      <ul>
        <authorisation-wrapper
          :resource="EMAIL_SECURITY"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <li key="manageRemediationOptions">
            <a
              class="tw-btn tw-btn-primary tw-flex tw-items-center"
              :data-test-id="`${tier}-go_to_redmediation`"
              :href="remediationHref"
              @click.prevent="
                () => {
                  if (isWriteAllowed) {
                    goToManageRemediationOptions();
                  }
                }
              "
            >
              <template v-if="isWriteAllowed">
                <span class="tw-p-3 tw-text-primary-grey-700">
                  <component
                    :is="SERVICE_ICON.PHISH_SERVICE"
                    :data-test-id="`${tier}-email_security-icon`"
                  />
                </span>
                <span
                  :class="
                    isSolutionActive(PHISH_SERVICE)
                      ? 'tw-font-semibold tw-text-primary-grey-700 hover:tw-text-primary-grey-700 hover:tw-cursor-pointer'
                      : 'tw-text-primary-grey-200 tw-font-normal hover:tw-cursor-default'
                  "
                  v-tippy="{
                    content: $t('no-email-security-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(PHISH_SERVICE),
                  }"
                >
                  {{ t("manage-remediation-options") }}
                </span>
              </template>
              <template v-else>
                <span class="tw-p-3 tw-text-primary-grey-200">
                  <component
                    :is="SERVICE_ICON.PHISH_SERVICE"
                    :data-test-id="`${tier}-email_security-icon`"
                  />
                </span>
                <span
                  :class="'tw-font-semibold tw-text-primary-grey-200 hover:tw-cursor-not-allowed'"
                >
                  {{ t("manage-remediation-options") }}
                </span>
              </template>
            </a>
          </li>
        </authorisation-wrapper>

        <authorisation-wrapper
          :resource="PHISHING_CAMPAIGNS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <li key="newPhishingSim">
            <div
              class="tw-btn tw-btn-primary tw-flex tw-items-center"
              :data-test-id="`${tier}-go_to_phish_simulation`"
              @click="
                () => {
                  if (isWriteAllowed) {
                    goToNewPhishingSim();
                  }
                }
              "
            >
              <template v-if="isWriteAllowed">
                <span class="tw-p-3 tw-text-primary-grey-700">
                  <component
                    :is="SERVICE_ICON.PHISHING_SIMULATION"
                    :data-test-id="`${tier}-phishing_simulation-icon`"
                  />
                </span>
                <span
                  :class="
                    isSolutionActive(PHISHING_SIMULATION)
                      ? 'tw-font-semibold tw-text-primary-grey-700 hover:tw-text-primary-grey-700 hover:tw-cursor-pointer'
                      : 'tw-text-primary-grey-200 tw-font-normal hover:tw-cursor-default'
                  "
                  v-tippy="{
                    content: $t('no-phs-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(PHISHING_SIMULATION),
                  }"
                >
                  {{ t("new-phishing-campaign") }}
                </span>
              </template>
              <template v-else>
                <span class="tw-p-3 tw-text-primary-grey-200">
                  <component
                    :is="SERVICE_ICON.PHISHING_SIMULATION"
                    :data-test-id="`${tier}-phishing_simulation-icon`"
                  />
                </span>
                <span
                  :class="'tw-font-semibold tw-text-primary-grey-200 hover:tw-cursor-not-allowed'"
                >
                  {{ t("new-phishing-campaign") }}
                </span>
              </template>
            </div>
          </li>
        </authorisation-wrapper>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { PHISHING_SIMULATION, PHISH_SERVICE } from "@/utils/services";
import { SERVICE_ICON } from "@/utils/services";
import { useActiveSolutions } from "@/composables/useActiveSolutions";
import { useRouter } from "vue-router";

import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";

import {
  AuthorisationWrapper,
  PHISHING_CAMPAIGNS,
  EMAIL_SECURITY,
  WRITE,
} from "@/authorisation";

const router = useRouter();
const { isSolutionActive } = useActiveSolutions();
const { t } = useI18n();

const { isMspRoute } = useIsMspRoute();

const tier = isMspRoute.value ? "msp" : "customer";

const tooltip = ref({
  content: t("quick-actions-dashboard"),
  placement: "top",
});
const goToManageRemediationOptions = (): void => {
  if (isSolutionActive(PHISH_SERVICE)) {
    router.push(remediationHref);
  }
};

const remediationHref = buildOrgUrl(
  "customer",
  null,
  "email_security/configuration/mail_flow",
);

const goToNewPhishingSim = (): void => {
  if (isSolutionActive(PHISHING_SIMULATION)) {
    router.push(
      buildOrgUrl(
        "customer",
        null,
        "phishing_simulation/overview-create_campaign",
      ),
    );
  }
};
</script>
